/**
 * Regex Validator Utility
 *
 * Provides functions for compiling and testing regex patterns from template
 * column definitions. Used by the TemplateValidator during normalization
 * (pre-compilation) and by the cell validation pipeline during CSV processing.
 *
 * All functions are pure and side-effect-free.
 */

/**
 * Compiles a regex pattern string into an anchored RegExp object.
 *
 * The pattern is wrapped with `^(?:...)$` to ensure full-match behavior —
 * the entire cell value must match, not just a substring. The non-capturing
 * group `(?:...)` prevents the anchors from interfering with the original
 * pattern's grouping.
 *
 * Used during template validation to:
 * 1. Verify the pattern is valid JavaScript regex syntax
 * 2. Pre-compile and anchor the pattern for reuse during cell validation
 *
 * The pattern string comes from the template's column `regex.pattern` field,
 * which uses JSON-safe double escaping (e.g., `"^EMP-\\\\d{4}$"` in JSON
 * becomes `"^EMP-\\d{4}$"` after JSON.parse).
 *
 * If the host app includes their own `^` or `$` anchors, they become
 * redundant but harmless inside the non-capturing group.
 *
 * @param pattern - A valid JavaScript regular expression string
 * @returns A compiled and anchored RegExp object
 * @throws Error if the pattern is not a valid regular expression
 *
 * @example
 * ```ts
 * const regex = compileRegex("EMP-\\d{4}");
 * // Returns: /^(?:EMP-\d{4})$/
 *
 * const regex2 = compileRegex("^EMP-\\d{4}$");
 * // Returns: /^(?:^EMP-\d{4}$)$/ — redundant anchors, still works
 *
 * compileRegex("[invalid");
 * // Throws: Error("Invalid regex pattern: [invalid — ...")
 * ```
 */
export function compileRegex(pattern: string): RegExp {
  try {
    return new RegExp(`^(?:${pattern})$`);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Invalid regex pattern: ${pattern} — ${message}`);
  }
}

/**
 * Tests a cell value against a pre-compiled RegExp.
 *
 * The regex is expected to be pre-compiled and anchored via `compileRegex`,
 * so this function is a simple `.test()` call with no additional processing.
 *
 * @param value - The cell value string to test
 * @param regex - A pre-compiled RegExp object (from compileRegex)
 * @returns `true` if the value matches the pattern, `false` otherwise
 *
 * @example
 * ```ts
 * const regex = compileRegex("EMP-\\d{4}");
 *
 * testRegex("EMP-1234", regex);        // true  — full match
 * testRegex("prefix-EMP-1234", regex); // false — partial match rejected
 * testRegex("EMP-12", regex);          // false — doesn't match pattern
 * ```
 */
export const testRegex = (value: string, regex: RegExp): boolean =>
  regex.test(value);
